import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const AdminInviteSimple = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [formSubmitted, setFormSubmitted] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        try {
            console.log('Submitting admin invitation form...');
            
            const response = await api.post('/direct/accept-invite', {
                token,
                name: formData.name,
                email: formData.email,
                password: formData.password
            });
            
            console.log('Admin invitation submitted:', response.data);
            
            // Hide form and show success message
            setFormSubmitted(true);
        } catch (error) {
            console.error('Error submitting admin invitation:', error);
            console.error('Error details:', error.response?.data);
            toast.error(error.response?.data?.message || 'Could not submit admin account for approval');
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Decorative elements */}
                <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-gradient-to-r from-[#FF3366]/20 to-[#FF6B4D]/20 filter blur-3xl"></div>
                <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-gradient-to-r from-[#FF6B4D]/20 to-[#FF3366]/20 filter blur-3xl"></div>
                
                <div className="bg-[#151515] rounded-xl p-8 shadow-lg border border-[#FF3366]/10 relative z-10">
                    {!formSubmitted ? (
                        <>
                            <div className="mb-8">
                                <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">
                                    Create Admin Account
                                </h1>
                                <p className="text-gray-400">
                                    Complete the form below to create your admin account with the provided invitation link.
                                </p>
                                {/* Token is hidden from user view but still used in the form submission */}
                            </div>
                            
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="name" className="block text-gray-300 mb-2 text-sm font-medium">Full Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter your full name"
                                        className="w-full p-3 bg-[#0A0A0A] border border-[#FF3366]/30 rounded-lg text-white focus:border-[#FF3366] focus:ring focus:ring-[#FF3366]/20 transition-all"
                                    />
                                </div>
                                
                                <div>
                                    <label htmlFor="email" className="block text-gray-300 mb-2 text-sm font-medium">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="Enter your email address"
                                        className="w-full p-3 bg-[#0A0A0A] border border-[#FF3366]/30 rounded-lg text-white focus:border-[#FF3366] focus:ring focus:ring-[#FF3366]/20 transition-all"
                                    />
                                </div>
                                
                                <div>
                                    <label htmlFor="password" className="block text-gray-300 mb-2 text-sm font-medium">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        placeholder="Create a secure password"
                                        className="w-full p-3 bg-[#0A0A0A] border border-[#FF3366]/30 rounded-lg text-white focus:border-[#FF3366] focus:ring focus:ring-[#FF3366]/20 transition-all"
                                    />
                                </div>
                                
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-gray-300 mb-2 text-sm font-medium">Confirm Password</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        placeholder="Confirm your password"
                                        className="w-full p-3 bg-[#0A0A0A] border border-[#FF3366]/30 rounded-lg text-white focus:border-[#FF3366] focus:ring focus:ring-[#FF3366]/20 transition-all"
                                    />
                                </div>
                                
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        className="w-full py-3 bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white rounded-lg font-medium hover:from-[#FF3366]/90 hover:to-[#FF6B4D]/90 transition-colors focus:ring focus:ring-[#FF3366]/50"
                                    >
                                        Create Admin Account
                                    </button>
                                </div>
                                
                                <p className="text-gray-500 text-sm text-center mt-4">
                                    Your account will be reviewed by a super admin before activation.
                                </p>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-[#FF3366]/20 to-[#FF6B4D]/20 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#FF3366]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] mb-4">Registration Complete!</h2>
                            <p className="text-gray-300 mb-4">
                                Your admin account registration has been successfully submitted.
                            </p>
                            <p className="text-gray-400 mb-6">
                                Your account is pending approval from a super admin. Once approved, you will be able to log in using your email and password.
                            </p>
                            <div className="border-t border-[#FF3366]/10 pt-6 mt-6">
                                <p className="text-gray-500">
                                    You can close this page now. You will receive an email notification once your account is approved.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminInviteSimple; 